/**
 * SearchBox — the search field: a gold magnifier in the dark plate, the text, and a clear button
 * while there is text. «Поиск»'s own since task 10 (the plate is task 66's), and since task 76
 * the field over the clips of «Видео» too — one field, so the two cannot drift apart.
 *
 * No «найти» button: the screen's store asks while the listener types (`store/search.ts`,
 * `store/video.ts`, by the rules of `lib/search-as-you-type.ts`). The text is 16 pixels at least,
 * or iOS zooms the page into the field; the clear button is a full touch target inside the plate
 * (`search-box.css`).
 */

import type { JSX, RefObject } from 'react';
import { useRef } from 'react';

import '../styles/search-box.css';

/** The longest query the field takes. The server allows more; nobody types more. */
export const MAX_QUERY_INPUT = 200;

export interface SearchBoxProps {
  /** Exactly what is in the field. */
  readonly value: string;
  readonly onChange: (text: string) => void;
  /** What a screen reader calls the field. */
  readonly label: string;
  readonly placeholder: string;
  readonly clearLabel: string;
  /** The field itself, for a screen that gives it the focus. */
  readonly fieldRef?: RefObject<HTMLInputElement | null> | undefined;
}

export function SearchBox({
  value,
  onChange,
  label,
  placeholder,
  clearLabel,
  fieldRef,
}: SearchBoxProps): JSX.Element {
  const own = useRef<HTMLInputElement | null>(null);
  const field = fieldRef ?? own;

  return (
    <div className="searchBox">
      <svg viewBox="0 0 24 24" aria-hidden="true" className="searchIcon">
        <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M20 20l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <input
        ref={field}
        className="searchField"
        type="text"
        role="searchbox"
        inputMode="search"
        enterKeyHint="search"
        aria-label={label}
        placeholder={placeholder}
        maxLength={MAX_QUERY_INPUT}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        onKeyDown={(event) => {
          // The results are already there: «Найти» on the keyboard only puts it away.
          if (event.key === 'Enter') event.currentTarget.blur();
        }}
      />
      {value !== '' && (
        <button
          type="button"
          className="searchClear"
          aria-label={clearLabel}
          onClick={() => {
            onChange('');
            field.current?.focus();
          }}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" className="searchClearIcon">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
}
